import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Suggestion } from 'use-places-autocomplete';

import { Control, Controller } from 'react-hook-form';
import { useOnClickOutside } from 'usehooks-ts';
import { useFloating, offset, flip, autoUpdate } from '@floating-ui/react';

import { TextInput, InputProps, Heading } from '@restworld/ui-ds-base';
import { italyBoundingBox, trackPlacesUsage, useGmapsScript } from '@restworld/utils-common';

import { FormLabelWrapper, HelperTextRElement } from '../..';
import { PlacesLocationBias, useRenderSuggestions } from './google-auto-complete-input.hooks';
import { DropdownElementWrapper, DropdownWrapper, MainWrapper } from './google-auto-complete-input.styles';

export type GooglePlacePrediction = Suggestion;

export type GoogleAutoCompleteDropdownItemProps = {
  suffixIcon?: React.ReactNode;
  dropdownItemStyle?: string;
  onClick?: (selectedItem?: string, restaurantName?: string, address?: string) => void;
  activeItem?: string;
  activeItemStyle?: string;
  itemTextStyle?: string;
  itemRenderVariant?: 'with_break_line' | 'without_break_line';
  item?: React.ReactNode;
  noResultElement?: React.ReactNode;
  loadingElement?: React.ReactNode;
};

interface IGoogleAddressInput {
  mainLabel?: string;
  formId: string;
  onSuggestionSelect?: (suggestion: GooglePlacePrediction) => void;
  placeholder?: string;
  disabled?: boolean;
  placesFilterTypes?: string[];
  title?: string;
  subTitle?: string;
  required?: boolean;
  testId?: string;
  formValue?: string;
  autoFocus?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any, any>;
  helperText?: string;
  dropdownTestId?: string;
  inputProps?: Partial<InputProps>;
  dropdownItemProps?: GoogleAutoCompleteDropdownItemProps;
  dropdownMainWrapperStyle?: string;
  dropdownSubWrapperStyle?: string;
  mainWrapperStyle?: string;
  withFloatingUI?: boolean;
  placesLocationBias?: PlacesLocationBias;
  onStatusChange?: (status: string) => void;
  /** Min typed chars before predictions fire, each debounced fetch is a billed Places request. */
  minSearchLength?: number;
  /**
   * Stable call-site id for Google Places usage telemetry (OpenObserve). Lets us see
   * which point of the app drives the most autocomplete consumption. Falls back to
   * `formId` when not set; pass an explicit value for repeatable field arrays so the
   * index does not create high-cardinality surfaces.
   */
  trackingSurface?: string;
}
const DROPDOWN_OFFSET = 4;

export const GoogleAutoCompleteInput = ({
  mainLabel,
  formId,
  placeholder,
  disabled,
  placesFilterTypes = ['address'],
  onSuggestionSelect,
  title,
  subTitle,
  required,
  testId,
  formValue,
  autoFocus = false,
  control,
  helperText,
  dropdownTestId,
  inputProps,
  dropdownItemProps,
  mainWrapperStyle,
  dropdownMainWrapperStyle,
  dropdownSubWrapperStyle,
  withFloatingUI = true,
  placesLocationBias = italyBoundingBox,
  onStatusChange,
  minSearchLength = 3,
  trackingSurface,
}: IGoogleAddressInput) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sessionTokenRef = useRef<unknown>(undefined);
  // Only user-typed input may trigger a billed predictions fetch: programmatic writes
  // (prefill from saved form values, writing back the selected suggestion) flip this off
  // before updating the input value.
  const shouldFetchRef = useRef(false);
  // One "autocomplete session" = from the first billed keystroke fetch until the user selects a
  // suggestion (or clears the field). Emit `autocomplete_session_start` once per session so we can
  // correlate sessions (this event) with the per-request Google billing (GCP request_count) and
  // derive fetches-per-session. Reset on selection and when the field is emptied.
  const sessionStartedRef = useRef(false);
  const [locationInputValue, onLocationInputChange] = useState('');
  const [cardOverlay, setCardOverlay] = useState(false);

  useEffect(() => {
    if (inputProps?.value) {
      shouldFetchRef.current = false;
      onLocationInputChange(inputProps?.value as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLocationInputChange]);

  const isScriptReady = useGmapsScript({ libraries: 'places' });

  const { renderSuggestions } = useRenderSuggestions({
    setCardOverlay,
    placesFilterTypes,
    onSuggestionSelect: (placesData) => {
      trackPlacesUsage({
        surface: trackingSurface ?? formId,
        api: 'autocomplete',
        meta: placesFilterTypes ? { types: placesFilterTypes.join(',') } : undefined,
      });
      onSuggestionSelect?.(placesData);
      sessionTokenRef.current = null;
      sessionStartedRef.current = false;
    },
    formId,
    locationInputValue,
    onLocationInputChange: (value: string) => {
      shouldFetchRef.current = false;
      onLocationInputChange(value);
    },
    dropdownItemProps,
    isScriptReady,
    sessionTokenRef,
    placesLocationBias,
    onStatusChange,
    shouldFetchRef,
    minSearchLength,
  });

  useOnClickOutside(dropdownRef, () => setCardOverlay(false));

  const suggestionList = useMemo(
    () =>
      locationInputValue ? (
        <DropdownWrapper data-cy="address-suggestion-dropdown" dropdownSubWrapperStyle={dropdownSubWrapperStyle}>
          {renderSuggestions()}
        </DropdownWrapper>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [dropdownSubWrapperStyle, locationInputValue, renderSuggestions]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom',
    open: cardOverlay,
    onOpenChange: setCardOverlay,
    middleware: [offset(DROPDOWN_OFFSET), flip({ padding: 5 })],
    whileElementsMounted: autoUpdate,
    transform: true,
  });

  useEffect(() => {
    if (inputRef.current && autoFocus) inputRef.current.focus();
  }, [autoFocus, inputRef]);

  useEffect(() => {
    if (formValue) {
      shouldFetchRef.current = false;
      onLocationInputChange(formValue);
    }
  }, [formValue, onLocationInputChange]);

  const handleInputChange = useCallback(
    (value: string) => {
      shouldFetchRef.current = true;
      const trimmedLength = value.trim().length;
      if (trimmedLength >= minSearchLength) {
        setCardOverlay(true);
        // First fetch of a new session: one billable autocomplete session opens here.
        if (!sessionStartedRef.current) {
          trackPlacesUsage({ surface: trackingSurface ?? formId, api: 'autocomplete_session_start' });
          sessionStartedRef.current = true;
        }
      } else {
        setCardOverlay(false);
        // Field cleared: the next typing starts a fresh session.
        if (trimmedLength === 0) sessionStartedRef.current = false;
      }
      inputProps?.onChange?.(value);
      onLocationInputChange(value);
    },
    [inputProps, minSearchLength, trackingSurface, formId]
  );

  const inputLocationProps: InputProps = useMemo(
    () => ({
      type: 'text',
      placeholder,
      id: formId,
      borderless: false,
      autoFocus: false,
      isFullwidth: true,
      disabled,
      value: locationInputValue,
      ...(helperText ? { helperText: <HelperTextRElement helperText={helperText} /> } : {}),
      ...inputProps,
      ref: inputRef,
      onChange: handleInputChange,
      testId: testId || 'google-auto-complete-input',
    }),
    [disabled, formId, handleInputChange, helperText, inputProps, locationInputValue, placeholder, testId]
  );

  const inputTextEl = useMemo(
    () => (
      <div ref={withFloatingUI ? refs.setReference : null} className="reference">
        <TextInput {...inputLocationProps} />
      </div>
    ),
    [inputLocationProps, refs.setReference, withFloatingUI]
  );

  const dropdownEl = useMemo(
    () =>
      cardOverlay && suggestionList ? (
        <div ref={withFloatingUI ? dropdownRef : null} data-cy={dropdownTestId}>
          <DropdownElementWrapper
            ref={withFloatingUI ? refs.setFloating : null}
            style={withFloatingUI ? floatingStyles : {}}
            dropdownMainWrapperStyle={dropdownMainWrapperStyle}
          >
            <div id="suggestion-4" className="flex flex-col gap-1">
              {title && (
                <div className="flex flex-col gap-1 p-4 border-b border-gray-200">
                  <Heading as="span" size={6} weight="semibold" color="primary" className="leading-[19.5px]">
                    {title}
                  </Heading>
                  <Heading as="span" size={6} weight="normal" color="muted" className="leading-[19.5px]">
                    {subTitle}
                  </Heading>
                </div>
              )}
              {suggestionList}
            </div>
          </DropdownElementWrapper>
        </div>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [
      cardOverlay,
      dropdownMainWrapperStyle,
      dropdownTestId,
      floatingStyles,
      refs.setFloating,
      subTitle,
      suggestionList,
      title,
      withFloatingUI,
    ]
  );

  return (
    <MainWrapper mainWrapperStyle={mainWrapperStyle}>
      <FormLabelWrapper isFullWidth id={formId} label={mainLabel}>
        {control ? (
          <Controller name={formId} control={control} rules={{ required }} render={() => inputTextEl} />
        ) : (
          inputTextEl
        )}
      </FormLabelWrapper>
      {dropdownEl}
    </MainWrapper>
  );
};

export default GoogleAutoCompleteInput;
